import { Fragment, ReactElement, useMemo } from 'react'
import {
  OrderResponse,
  OrderResponseDetail,
} from '../../../../../src/api/cart/interfaces'
import { flatten } from 'lodash-es'
import { getPartsTableContent } from '../../orderConfirmationUtils'
import { defaultTheme } from '../../../../../src/theme/index'
import styled from 'styled-components'
import { Divider } from '@mui/material'
import { I18n, MY_PLACE_SELLER } from 'common'
import { CartFooter } from './order/CartFooter'
import { PartsTableContent } from './order/PartsTableContent'
import { SpecificationConfirmationItems } from './SpecificationConfirmationItems'
import { Table } from './SharedCssStyle'
import { LaborConfirmationItems } from './LaborConfirmationItems'
import { VehicleInfoProps } from '../../../../../src/features/SummaryPanel/Interfaces'
import { PoNumberErrorMessageBody } from '../../../../../src/features/cart/components/AvailabilityErrors/components/PoNumberErrorMessage'
import { breakpoints } from 'src/theme/breakpoints'
import OrderConfirmationPartsTableHeader from './OrderConfirmationPartsTableHeader'
import DDSAdvancedTracker from 'src/features/orderHistory/components/ddsAdvancedTracker/DDSAdvancedTracker'
import { OrderConfirmationVehicleHeader } from './OrderConfirmationVehicleHeader'
import { ISeller } from 'src/services/MultiSellerServicesProvider'
import { useUserStore } from 'src/store/user/UserStore'
import { Config } from 'src/config/ConfigManager'
import ErrorIcon from '@mui/icons-material/Error'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { IS_MULTI_SELLER_ENABLED } from '../../../../../src/config/ConfigManager'
import { formatVehicleShortDisplayWithOutEngine } from 'src/helpers/utils'
import { Thumbnail } from '../../../../../src/ui-components/products/Thumbnail'

export interface CartItemsProps {
  showYourCost: boolean
  orderDetails: OrderResponseDetail
  vehicleInfo: VehicleInfoProps
  sellersData: ISeller[]
  orderResponse?: OrderResponse
}

export const CartItems = (props: CartItemsProps): ReactElement => {
  const { showYourCost, orderDetails, vehicleInfo, sellersData } = props

  const { vehicle, comment } = orderDetails

  const userStore = useUserStore()
  const { isDisplayMultiSellerLogos } = useUiStateStore()
  const ddsOn = userStore?.preferences?.dds === 'true' && Config.ddsEnabled
  const advancedTrackerEnabled =
    ddsOn && userStore?.preferences?.shipmentStatusTrackerEnabled === 'true'
  const orders = useMemo(() => {
    const flattenedOrders = flatten(
      orderDetails.locations.map((location) =>
        location.orders.map((order) => ({
          ...order,
          coverageKey: location.coverageKey,
          noteToStore: location?.noteToStore
            ? location?.noteToStore
            : orderDetails.noteToStore,
          poNumber: location?.poNumber
            ? location?.poNumber
            : orderDetails.poNumber,
          specifications: orderDetails.specifications,
          laborData: orderDetails.laborData,
        }))
      )
    )
    return flattenedOrders.sort((a, b) => {
      if (a.orderError && !b.orderError) return -1
      if (!a.orderError && b.orderError) return 1
      return 0
    })
  }, [orderDetails])

  const acnchorTagWithoutHash = vehicleInfo.anchorTag.replace('#', '')

  function getSellerLogoUrl(coverageKey: number | string | undefined): string {
    const match = sellersData?.find((item) => item.coverageKey === coverageKey)
    return match
      ? match.logoUrl
      : coverageKey === ''
        ? MY_PLACE_SELLER.logoUrl
        : ''
  }

  function getSellerName(coverageKey: number | string | undefined): string {
    const match = sellersData?.find((item) => item.coverageKey === coverageKey)
    return match
      ? match.sellerName
      : coverageKey === ''
        ? `${MY_PLACE_SELLER.sellerName} Order`
        : ''
  }

  const vehicleName = formatVehicleShortDisplayWithOutEngine(vehicle)

  const getSeller = (coverageKey: number | string | undefined): ISeller => {
    const seller = sellersData?.find((s) => s.coverageKey === coverageKey)
    return seller ?? MY_PLACE_SELLER
  }

  return (
    <Wrapper id={acnchorTagWithoutHash}>
      <OrderConfirmationVehicleHeader
        vehicleName={vehicleName}
        personalNote={comment}
        customerName={
          orderDetails?.locations[0]?.orders[0]?.deliveryDetail?.name
        }
        cartId={orderDetails.cartId}
      />
      {orders.map((order) => {
        const isMyPlaceOrder = !order?.coverageKey
        return (
          <Fragment key={order.orderNumber}>
            {order?.orderError && (
              <PoNumberErrorMessageBody errorMessage={order?.errorMsg} />
            )}
            <OrderConfirmationMainHeaderWrapper>
              <HeaderLogoWrapper>
                <HeaderLogo>
                  {(isDisplayMultiSellerLogos() || !order?.coverageKey) &&
                    IS_MULTI_SELLER_ENABLED && (
                      <div>
                        <Thumbnail
                          urlOrBase64={getSellerLogoUrl(order.coverageKey)}
                          alt="Seller"
                          width={100}
                          height={36}
                          placeholder
                        />
                      </div>
                    )}
                  <ImageTextHeader>
                    {getSellerName(order.coverageKey)}
                  </ImageTextHeader>
                </HeaderLogo>
                {order?.orderError && (
                  <div>
                    <OrderFiledWrapper>
                      <ErrorMessageIcon>
                        <ErrorIcon
                          fontSize="medium"
                          sx={{ color: '#D9001B' }}
                        />
                      </ErrorMessageIcon>
                      <I18n name="orderFailed" />
                    </OrderFiledWrapper>
                  </div>
                )}
                {advancedTrackerEnabled &&
                  !order?.orderError &&
                  isMyPlaceOrder && (
                    <div>
                      {' '}
                      <DdsStepsDesktop>
                        <DDSAdvancedTracker state={1} clickable={false} />
                      </DdsStepsDesktop>
                      <DdsStepsMobile>
                        <DDSAdvancedTracker
                          state={1}
                          mobile
                          clickable={false}
                        />
                      </DdsStepsMobile>
                    </div>
                  )}
                {advancedTrackerEnabled &&
                  !order?.orderError &&
                  !isMyPlaceOrder && (
                    <div>
                      <OrderFiledWrapper>
                        <ErrorMessageIcon>
                          <AddBusinessIcon
                            fontSize="medium"
                            sx={{ color: '#F9C100' }}
                          />
                        </ErrorMessageIcon>
                        <I18n name="ordered" />
                      </OrderFiledWrapper>
                    </div>
                  )}
              </HeaderLogoWrapper>
              <div>
                <TextWrapper>
                  <StyledText>
                    <I18n name="orderNumber" />:{' '}
                    <OrderHeaderText>{order?.orderNumber}</OrderHeaderText>
                  </StyledText>

                  <DividerLine
                    orientation="vertical"
                    style={{
                      borderColor: '#E5E4E1',
                      borderWidth: '1px',
                      opacity: 1,
                    }}
                  />
                  <StyledText>
                    <I18n name="confirmationID" />:{' '}
                    <OrderHeaderText>
                      {order?.parts[0]?.orderConfirmationNbr}
                    </OrderHeaderText>
                  </StyledText>
                  <DividerLine
                    orientation="vertical"
                    style={{
                      borderColor: '#E5E4E1',
                      borderWidth: '1px',
                      opacity: 1,
                    }}
                  />
                  <StyledText>
                    <I18n name="poNumber" />:{' '}
                    <OrderHeaderText>{order?.poNumber}</OrderHeaderText>
                  </StyledText>
                  <DividerLine
                    orientation="vertical"
                    style={{
                      borderColor: '#E5E4E1',
                      borderWidth: '1px',
                      opacity: 1,
                    }}
                  />
                  <StyledText>
                    <I18n name="noteToStore" />:{' '}
                    <OrderHeaderText>{order?.noteToStore}</OrderHeaderText>
                  </StyledText>
                  {isMyPlaceOrder && (
                    <>
                      <DividerLine
                        orientation="vertical"
                        style={{
                          borderColor: '#E5E4E1',
                          borderWidth: '1px',
                          opacity: 1,
                        }}
                      />
                      <StyledText>
                        <I18n name="shipTo" />:{' '}
                        <OrderHeaderText>
                          {order?.deliveryDetail?.address
                            ? `${order?.deliveryDetail?.address?.line1},${order?.deliveryDetail?.address?.city},${order?.deliveryDetail?.address?.region} ${order?.deliveryDetail?.address?.postalCode}`
                            : ''}
                        </OrderHeaderText>
                      </StyledText>
                    </>
                  )}
                </TextWrapper>
              </div>
            </OrderConfirmationMainHeaderWrapper>
            <OrderTypeHeader>
              <ProductsHeader>
                <I18n name="products" />
              </ProductsHeader>
              {isMyPlaceOrder && (
                <OrderTypeHeaderWrapper>
                  <TextWrapper>
                    {order?.deliveryDetail?.deliveryMethod && (
                      <StyledText>
                        <I18n name="orderType" />:{' '}
                        <OrderHeaderText>
                          {order.deliveryDetail.deliveryMethod}
                        </OrderHeaderText>
                      </StyledText>
                    )}

                    {order?.deliveryDetail?.carrier && (
                      <>
                        <DividerLine
                          orientation="vertical"
                          style={{
                            borderColor: '#E5E4E1',
                            borderWidth: '1px',
                            opacity: 1,
                          }}
                        />
                        <StyledText>
                          <I18n name="transport" />:{' '}
                          <OrderHeaderText>
                            {order.deliveryDetail.carrier}
                          </OrderHeaderText>
                        </StyledText>
                      </>
                    )}
                  </TextWrapper>
                </OrderTypeHeaderWrapper>
              )}
            </OrderTypeHeader>
            <Table>
              <OrderConfirmationPartsTableHeader
                columns={[
                  { name: 'item', align: 'center', visible: true },
                  { name: 'location', align: 'left', visible: true },
                  { name: 'yourCost', align: 'left', visible: showYourCost },
                  { name: 'listPrice', align: 'left', visible: true },
                  { name: 'qty', align: 'left', visible: true },
                  { name: 'total', align: 'left', visible: showYourCost },
                ]}
              />
              <PartsTableContent
                showYourCost={showYourCost}
                parts={getPartsTableContent(order)}
                seller={getSeller(order?.coverageKey)}
              />
            </Table>
          </Fragment>
        )
      })}
      {orderDetails?.specifications && (
        <SpecificationConfirmationItems
          orderSpecifications={orderDetails.specifications}
        />
      )}
      {orderDetails?.laborData && (
        <LaborConfirmationItems laborItems={orderDetails.laborData} />
      )}
      {showYourCost && <CartFooter total={vehicleInfo?.totalCost ?? 0} />}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  min-height: 400px;
  margin-top: 15px;
  font-family: Roboto, serif;
  background: ${defaultTheme.colors.white};
`
const DividerLine = styled(Divider)`
  height: auto;
  width: 1px;
  margin-left: 10px;
  margin-right: 10px;
  border-color: rgb(229 228 225);
  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`
const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    font-weight: 800px;
    row-gap: 5px;
  }
`
const StyledText = styled.div`
  text-align: left;
  width: fit-content;
  font: 'Roboto';
  font-weight: 700;
  font-size: 14px;
  line-heigth: 24px;
`
const OrderFiledWrapper = styled.div`
  width: max-content;
  border: 1px solid rgba(229, 228, 225, 1);
  padding: 4px 12px;
  margin-left: auto;
  display: inline-flex;
  margin-left: auto;
  max-height: 32px;
  align-items: center;
  border-radius: 4px;
  background-color: #ffffff;
`
const ErrorMessageIcon = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`
const DdsStepsDesktop = styled.div`
  display: block;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`

const DdsStepsMobile = styled.div`
  display: none;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: block;
    align-self: flex-start;
  }
`

const OrderTypeHeaderWrapper = styled.div`
  display: flex;
  gap: 8px;
`
const OrderTypeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  background-color: #fafafa;
`
const ProductsHeader = styled.div`
  font-family: 'Oswald';
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
`
const OrderConfirmationMainHeaderWrapper = styled.div`
  display: flex;
  padding: 26px 24px;
  background-color: #fafafa;
  flex-direction: column;
  row-gap: 16px;
`
const HeaderLogoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`
const HeaderLogo = styled.div`
  display: flex;
  gap: 24px;
`
const ImageTextHeader = styled.div`
  font-family: 'Oswald';
  font-weight: 700;
  font-size: 24px;
  line-height: 34px;
`
const OrderHeaderText = styled.span`
  font-family: 'Roboto';
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
`
